<template>
  <div
    id="custom-select-container"
  >
    <div
      id="custom-select-element"
      ref="custom-select-element"
      :class="[`custom-select ${customSizing} custom-select-element`, {'is-focused': isFocused, 'disabled': disabled}]"
      @click="handleClickSelect($event)"
      tabindex="0"
      data-role="custom-select"
    >
      <p
        id="selected-option"
        ref="selected-option"
        :class="['mb-0 w-100 ellipsis', {'text-muted': getValue === placeholder}]"
        data-role="custom-select-selected"
        data-test="selected-option"
      >
        {{ selectedOptionText }}
      </p>
      <ul
        v-show="isOpen"
        id="list-options"
        :class="['card rounded-0']"
        @mouseenter="setIsMouseOverList(true)"
        @mouseleave="setIsMouseOverList(false)"
        data-role="custom-select-options-list"
      >
        <li
          v-for="(option, index) in optionsList"
          :key="index"
          :class="['list-option', {'highlighted': isHighlighted(option)}]"
          @mouseover="setHoveredIndex(index)"
          @click="handleClickOption(option)"
          data-role="custom-select-option"
        >
          {{ option[textFields] }}
        </li>
      </ul>
    </div>
    <div
      :class="{'is-paused': disabled}"
    />
  </div>
</template>
<script>
import objectHelper from '@/core/mixins/objectHelper';

function initialData() {
  return {
    isOpen: false,
    isFocused: false,
    isMouseOverList: false,
    hoveredIndex: null,
  };
}
export default {
  name: 'BaseSelect',
  props: {
    size: {
      type: String,
      required: false,
      default: '',
    },
    value: {
      type: [String, Number, null],
      required: false,
      default: null,
    },
    placeholder: {
      type: [String, Number, null],
      required: false,
      default: '',
    },
    options: {
      type: Array,
      required: true,
      default: () => [
        { value: 0, text: '' },
      ],
    },
    textFields: {
      type: String,
      required: false,
      default: 'text',
    },
    valueFields: {
      type: String,
      required: false,
      default: 'value',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  mixins: [
    objectHelper,
  ],
  data() {
    return initialData();
  },
  computed: {
    customSizing() {
      return this.size ? `custom-select-${this.size}` : '';
    },
    selectedOptionText() {
      if (this.optionsList.length === 0) return 'Select';
      const option = this.optionsList.find((listOption) => listOption[this.valueFields] === this.getValue);
      return option ? option[this.textFields] : this.placeholder;
    },
    getValue() {
      return this.value
        || this.placeholder;
    },
    optionsList() {
      const optionsList = [];
      for (let index = 0; index < this.options.length; index++) {
        const option = this.options[index];
        if (this.isObject(option)) optionsList.push(option);
        else {
          optionsList.push({
            [this.valueFields]: option,
            [this.textFields]: option,
          });
        }
      }
      return optionsList;
    },
  },
  methods: {
    handleClickSelect(event) {
      event.stopImmediatePropagation();
      this.isOpen = !this.isOpen;
      if (this.isOpen) this.handleFocus();
    },
    handleClickOption(option) {
      this.$emit('change', option);
    },
    handleKeyDown(event) {
      event.preventDefault();
      const allowedKeys = ['Enter', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft'];
      if (!allowedKeys.includes(event.key)) this.handleBlur();
      if (event.key === 'Enter') {
        if (!this.isOpen) this.isOpen = true;
        else {
          this.handleClickOption(this.optionsList[this.hoveredIndex]);
          this.isOpen = false;
        }
        return;
      }
      if (event.key === 'ArrowUp') {
        if (!this.isOpen) this.isOpen = true;
        else if (this.hoveredIndex !== 0) this.hoveredIndex--;
      }
      if (event.key === 'ArrowDown') {
        if (!this.isOpen) this.isOpen = true;
        else if (this.hoveredIndex < this.optionsList.length - 1) this.hoveredIndex++;
      }
    },
    handleFocus() {
      if (this.isFocused) return;
      this.isFocused = true;
      this.$emit('focus');
      document.addEventListener('click', (documentEvent) => {
        if (!this.isFocused) return;
        if (!documentEvent.target?.dataset?.role?.includes('custom-select')) {
          this.isOpen = false;
          this.isFocused = false;
          this.handleBlur();
        }
      }, { once: true });
      this.setHoveredIndex();
    },
    handleBlur() {
      this.$emit('blur');
      this.resetData();
      this.$refs['custom-select-element'].blur();
      this.setHoveredIndex();
    },
    isHighlighted(option) {
      if (typeof this.hoveredIndex === 'number') return option[this.valueFields] === this.optionsList[this.hoveredIndex][this.valueFields];
      return option[this.valueFields] === this.getValue;
    },
    setIsMouseOverList(isOver) {
      this.isMouseOverList = isOver;
    },
    setHoveredIndex(index = null) {
      if (typeof index !== 'number') {
        const optionIndex = this.optionsList.findIndex((option) => option[this.valueFields] === this.getValue);
        this.hoveredIndex = optionIndex !== -1 ? optionIndex : 0;
      } else this.hoveredIndex = index;
    },
    resetData() {
      Object.assign(this.$data, initialData());
    },
  },
  mounted() {
    const selectElement = this.$refs['custom-select-element'];
    selectElement.addEventListener('keydown', this.handleKeyDown);
    if (this.getValue) this.setHoveredIndex();
  },
  beforeDestroy() {
    const selectElement = this.$refs['custom-select-element'];
    selectElement.removeEventListener('keydown', this.handleKeyDown);
  },
};
</script>
<style scoped lang="scss">
#custom-select-container {
  position: relative;
}
.custom-select-element {
  position: relative;
  display: flex !important;
  align-items: center;
  &.is-focused {
    border-color: $link;
    outline: .2rem solid rgba(0, 123, 255, 0.25);
  }
  &.disabled {
    color: #6c757d;
    background-color: #e9ecef;
  }
}
#list-options {
  position: absolute;
  width: 100%;
  top: 102%;
  left: 0;
  z-index: 1;
  list-style: none;
  padding: 0;
  border: 1px solid $gray-500;
  box-shadow: 0px 3px 8px rgba(0,0,0,0.35);
  .list-option {
    padding: .25rem .75rem;
    &.highlighted {
      background-color: $link;
      color: white;
    }
  }
}
.is-paused {
  position: absolute;
  cursor: not-allowed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
